import * as React from "react"

/*
   Which PDF this viewer reads on a label page: the label with its marks, the master it was compared
   against, or both side by side. The label by default. Side by side, whether the master follows the
   label's page, on by default. Both remembered per browser, like the scope switch; a browser that
   refuses storage still gets the choice for the life of the page.
*/

export type PdfView = "label" | "master" | "side_by_side"

export const PDF_VIEWS: readonly PdfView[] = ["label", "master", "side_by_side"]

const STORAGE_KEY = "pyxis.evalRuns.pdfView"
const listeners = new Set<() => void>()
/** The choice for this page, used once storage has refused a write. */
let fallback: PdfView | null = null

const isView = (v: string | null): v is PdfView => v !== null && PDF_VIEWS.some((p) => p === v)

function read(): PdfView {
  if (fallback !== null) return fallback
  try {
    const stored = globalThis.localStorage.getItem(STORAGE_KEY)
    return isView(stored) ? stored : "label"
  } catch {
    return "label"
  }
}

function write(value: PdfView): void {
  try {
    if (value === "label") globalThis.localStorage.removeItem(STORAGE_KEY)
    else globalThis.localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Storage refused: keep the choice in memory for the life of the page.
    fallback = value
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function usePdfView(): [PdfView, (value: PdfView) => void] {
  const value = React.useSyncExternalStore(subscribe, read, () => "label" as const)
  return [value, write]
}

const FOLLOW_KEY = "pyxis.evalRuns.followLabel"
let followFallback: boolean | null = null

function readFollow(): boolean {
  if (followFallback !== null) return followFallback
  try {
    return globalThis.localStorage.getItem(FOLLOW_KEY) !== "0"
  } catch {
    return true
  }
}

function writeFollow(value: boolean): void {
  try {
    if (value) globalThis.localStorage.removeItem(FOLLOW_KEY)
    else globalThis.localStorage.setItem(FOLLOW_KEY, "0")
  } catch {
    followFallback = value
  }
  for (const listener of listeners) listener()
}

export function useFollowLabel(): [boolean, (value: boolean) => void] {
  const value = React.useSyncExternalStore(subscribe, readFollow, () => true)
  return [value, writeFollow]
}
